import { createSelector } from "reselect";
import type {
  FeedFilterOption,
  FeedItem,
  FeedPortfolio,
  FeedPost,
} from "../../types/feed.ts";
import type { FeedItemOrderEntry } from "./reducer.ts";

type FeedViewState = {
  selectedOption: FeedFilterOption;
  order: string | null;
  type: string | null;
};

type FeedDataState = {
  feedView: FeedViewState;
  posteos?: FeedPost[];
  ownPosteos?: FeedPost[];
  allPortfolios?: FeedPortfolio[];
  itemOrder?: FeedItemOrderEntry[];
};

type RootState = {
  feed?: FeedDataState;
  general?: { userData?: { id?: number | string } };
};

function sortByCreatedAtStable<T extends { createdAt?: string }>(items: T[]): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const tA = new Date(a.item.createdAt ?? 0).getTime()
      const tB = new Date(b.item.createdAt ?? 0).getTime()
      const diff = tB - tA
      if (diff !== 0) return diff
      return a.index - b.index
    })
    .map(({ item }) => item)
}

function mixPostsAndPortfolios(
  posts: FeedPost[] | undefined,
  portfolios: FeedPortfolio[] | undefined
): FeedItem[] {
  const postsWithType = (posts || []).map((post) => ({ ...post, itemType: "post" as const }))
  const portfoliosWithType = (portfolios || []).map((portfolio) => ({
    ...portfolio,
    itemType: "portfolio" as const,
  }))
  const allItems = [...postsWithType, ...portfoliosWithType]
  return sortByCreatedAtStable(allItems)
}

function postsFirstPortfoliosByDate(
  posts: FeedPost[] | undefined,
  portfolios: FeedPortfolio[] | undefined
): FeedItem[] {
  const postsWithType = (posts || []).map((post) => ({ ...post, itemType: "post" as const }))
  const portfoliosWithType = (portfolios || []).map((portfolio) => ({
    ...portfolio,
    itemType: "portfolio" as const,
  }))
  const sortedPortfolios = sortByCreatedAtStable(portfoliosWithType)
  return [...postsWithType, ...sortedPortfolios]
}

export function selectFeedView(state: RootState): FeedViewState {
  return (
    state.feed?.feedView ?? {
      selectedOption: "Mostrar",
      order: null,
      type: null,
    }
  );
}

export function computeFeedItemsFromData(
  posteos: FeedPost[],
  ownPosteos: FeedPost[],
  allPortfolios: FeedPortfolio[],
  selectedOption: FeedFilterOption,
  userId: number | string | undefined
): FeedItem[] {
  let baseItems: FeedItem[] = [];

  switch (selectedOption) {
    case "propios": {
      const userPortfolios = allPortfolios.filter((portfolio) => portfolio.user?.id === userId);
      baseItems = mixPostsAndPortfolios(ownPosteos, userPortfolios);
      break;
    }
    case "productos": {
      const productos = allPortfolios.filter((portfolio) => portfolio.type === "producto");
      baseItems = sortByCreatedAtStable(
        productos.map((p) => ({ ...p, itemType: "portfolio" as const }))
      );
      break;
    }
    case "servicios": {
      const servicios = allPortfolios.filter((portfolio) => portfolio.type === "servicio");
      baseItems = sortByCreatedAtStable(
        servicios.map((p) => ({ ...p, itemType: "portfolio" as const }))
      );
      break;
    }
    case "posteos":
      baseItems = sortByCreatedAtStable(
        posteos.map((post) => ({ ...post, itemType: "post" as const }))
      );
      break;
    case "recomendados":
      baseItems = postsFirstPortfoliosByDate(posteos, allPortfolios);
      break;
    case "recientes":
    case "Mostrar":
    default:
      baseItems = mixPostsAndPortfolios(posteos, allPortfolios);
      break;
  }

  return baseItems;
}

export function computeItemOrder(
  posteos: FeedPost[],
  ownPosteos: FeedPost[],
  allPortfolios: FeedPortfolio[],
  selectedOption: FeedFilterOption,
  userId: number | string | undefined
): FeedItemOrderEntry[] {
  const items = computeFeedItemsFromData(
    posteos,
    ownPosteos,
    allPortfolios,
    selectedOption,
    userId
  );
  return items.map((item) => ({
    type: item.itemType ?? (item.id != null ? "post" : "portfolio"),
    id: item.id as number,
  }));
}

const selectPosteos = (state: RootState) => state.feed?.posteos;
const selectOwnPosteos = (state: RootState) => state.feed?.ownPosteos;
const selectAllPortfolios = (state: RootState) => state.feed?.allPortfolios;
const selectItemOrder = (state: RootState) => state.feed?.itemOrder;
const selectSelectedOption = (state: RootState) =>
  selectFeedView(state).selectedOption;
const selectUserId = (state: RootState) => state.general?.userData?.id;

export const selectFeedItemsFromOrder = createSelector(
  [
    selectPosteos,
    selectOwnPosteos,
    selectAllPortfolios,
    selectItemOrder,
    selectSelectedOption,
    selectUserId,
  ],
  (
    posteos,
    ownPosteos,
    allPortfolios,
    itemOrder,
    selectedOption,
    userId
  ): FeedItem[] => {
    const posts = posteos ?? [];
    const own = ownPosteos ?? [];
    const portfolios = allPortfolios ?? [];
    const order = itemOrder ?? [];

    if (order.length === 0) {
      return computeFeedItemsFromData(
        posts,
        own,
        portfolios,
        selectedOption,
        userId
      );
    }

    const postById = new Map<number, FeedPost>(
      [...posts, ...own].map((p) => [p.id, p])
    );
    const portfolioById = new Map<number, FeedPortfolio>(
      portfolios.map((p) => [p.id, p])
    );

    const result: FeedItem[] = [];
    for (const entry of order) {
      if (entry.type === "post") {
        const post = postById.get(entry.id);
        if (post) result.push({ ...post, itemType: "post" });
      } else {
        const portfolio = portfolioById.get(entry.id);
        if (portfolio) result.push({ ...portfolio, itemType: "portfolio" });
      }
    }
    return result;
  }
);

export function selectFeedItems(state: RootState): FeedItem[] {
  return selectFeedItemsFromOrder(state);
}
